// src/pages/ClinicalDataCollector/components/output/ProviderEncounterTable.jsx

import React, { useMemo, useState, useContext } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
} from '@mui/material';

import PersonIcon from '@mui/icons-material/Person';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PhoneIcon from '@mui/icons-material/Phone';
import NotesIcon from '@mui/icons-material/Notes';

import { AdvancedMdContext } from '../../../../context/AdvancedMD/AdvancedMdContext';

// ======================= Helpers de texto =======================

const cleanText = (value) =>
  (value || '')
    .replace(/\s+/g, ' ')
    .trim();

const truncate = (text, max = 350) => {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 3)}...` : text;
};

// ======================= Normalizadores por plantilla =======================
//
// 1) Follow Up Exam 2.0
//

const buildFollowUpExam20Summary = (fields) => {
  const parts = [];

  if (cleanText(fields.ChiefComplaints)) {
    parts.push(`Chief complaint: ${cleanText(fields.ChiefComplaints)}`);
  }
  if (cleanText(fields.CurrentMedications)) {
    parts.push(`Medications: ${cleanText(fields.CurrentMedications)}`);
  }
  if (cleanText(fields.Assessments)) {
    parts.push(`Assessment: ${cleanText(fields.Assessments)}`);
  }
  if (cleanText(fields.Treatment)) {
    parts.push(`Plan: ${cleanText(fields.Treatment)}`);
  }

  return truncate(parts.join(' | '));
};

const normalizeFollowUpExam20 = (raw) => {
  const fields = raw.fields || {};

  const providerName =
    cleanText(raw.signedByUser) ||
    cleanText(raw.userName) ||
    'Unknown Provider';

  return {
    providerId: null,
    providerName,
    encounterDate: raw.noteDateTime || null,
    noteType: 'Follow Up Exam 2.0',
    templateName: raw.templateName,
    summary: buildFollowUpExam20Summary(fields),
    diagnosisText: cleanText(fields.Assessments),
    planText: cleanText(fields.Treatment),
    phone: '', // esta plantilla no trae teléfono
    raw,
  };
};

//
// 2) Annual Wellness 2.0
//

const buildAdlSummary = (fields) => {
  const keys = Object.keys(fields || {}).filter(
    (k) => k.startsWith('ADL_') && !k.endsWith('Comments')
  );

  if (!keys.length) return '';

  const needsHelp = keys.filter((k) => {
    const v = cleanText(fields[k]);
    return v && v !== 'No Assistance';
  });

  if (!needsHelp.length) {
    return 'ADL: Independent in activities of daily living.';
  }

  return `ADL: Needs assistance in ${needsHelp.length} area(s).`;
};

const buildAnnualWellness20Summary = (fields) => {
  const parts = [];

  if (cleanText(fields.HistoryofPresentIllness)) {
    parts.push(`HPI: ${cleanText(fields.HistoryofPresentIllness)}`);
  }
  if (cleanText(fields.PreventiveMedicine)) {
    parts.push(`Preventive care: ${cleanText(fields.PreventiveMedicine)}`);
  }
  if (cleanText(fields.GeriatricWellness_ExerciseYesOrNo)) {
    parts.push(
      `Exercise: ${cleanText(fields.GeriatricWellness_ExerciseYesOrNo)}`
    );
  }

  const adl = buildAdlSummary(fields);
  if (adl) parts.push(adl);

  return truncate(parts.join(' | '));
};

const normalizeAnnualWellness20 = (raw) => {
  const fields = raw.fields || {};

  const providerName =
    cleanText(raw.signedByUser) ||
    cleanText(raw.userName) ||
    'Unknown Provider';

  return {
    providerId: null,
    providerName,
    encounterDate: raw.noteDateTime || null,
    noteType: 'Annual Wellness 2.0',
    templateName: raw.templateName,
    summary: buildAnnualWellness20Summary(fields),
    diagnosisText: cleanText(fields.Assessments),
    planText: cleanText(fields.Treatment),
    phone: '',
    raw,
  };
};

//
// 3) _Hospital Discharge Follow Up
//

const buildHospitalDischargeSummary = (fields) => {
  const parts = [];

  if (cleanText(fields.ReasonforAppointment)) {
    parts.push(`Reason: ${cleanText(fields.ReasonforAppointment)}`);
  }
  if (cleanText(fields.HistoryofPresentIllness)) {
    parts.push(`HPI: ${cleanText(fields.HistoryofPresentIllness)}`);
  }
  if (cleanText(fields.HistoryofPresentIllness1)) {
    parts.push(`TCM: ${cleanText(fields.HistoryofPresentIllness1)}`);
  }
  if (cleanText(fields.Assessments)) {
    parts.push(`Assessment: ${cleanText(fields.Assessments)}`);
  }
  if (cleanText(fields.Treatment)) {
    parts.push(`Plan: ${cleanText(fields.Treatment)}`);
  }
  if (cleanText(fields.FollowUp)) {
    parts.push(`Follow up: ${cleanText(fields.FollowUp)}`);
  }

  return truncate(parts.join(' | '));
};

const normalizeHospitalDischargeFollowUp = (raw) => {
  const fields = raw.fields || {};

  const providerName =
    cleanText(raw.signedByUser) ||
    cleanText(raw.userName) ||
    'Unknown Provider';

  const phone = cleanText(fields.location1); // aquí viene el teléfono

  return {
    providerId: null,
    providerName,
    encounterDate: raw.noteDateTime || null,
    noteType: 'Hospital Discharge Follow Up',
    templateName: raw.templateName,
    summary: buildHospitalDischargeSummary(fields),
    diagnosisText: cleanText(fields.Assessments),
    planText: cleanText(fields.Treatment),
    phone,
    raw,
  };
};

//
// 4) TELEHEALTH_2.0
//

const buildTelehealthSummary = (fields) => {
  const parts = [];

  if (cleanText(fields.HPI_Description)) {
    parts.push(`HPI: ${cleanText(fields.HPI_Description)}`);
  }
  if (cleanText(fields.DX_Diagnosis)) {
    parts.push(`Dx: ${cleanText(fields.DX_Diagnosis)}`);
  }
  if (cleanText(fields.AP_VisitSummary)) {
    parts.push(`Summary: ${cleanText(fields.AP_VisitSummary)}`);
  }
  if (cleanText(fields.ED_NutritionCounsel)) {
    parts.push(`Nutrition: ${cleanText(fields.ED_NutritionCounsel)}`);
  }
  if (cleanText(fields.ED_PhysicalActivityCounsel)) {
    parts.push(`Activity: ${cleanText(fields.ED_PhysicalActivityCounsel)}`);
  }

  return truncate(parts.join(' | '));
};

const normalizeTelehealth20 = (raw) => {
  const fields = raw.fields || {};

  const providerName =
    cleanText(raw.signedByUser) ||
    cleanText(raw.userName) ||
    'Unknown Provider';

  return {
    providerId: null,
    providerName,
    encounterDate: raw.noteDateTime || null,
    noteType: 'Telehealth 2.0',
    templateName: raw.templateName,
    summary: buildTelehealthSummary(fields),
    diagnosisText: cleanText(fields.DX_Diagnosis),
    planText: cleanText(fields.AP_VisitSummary),
    phone: '',
    raw,
  };
};

// ======================= Componente principal =======================

const ProviderEncounterTable = () => {
  const advancedMdContext = useContext(AdvancedMdContext) || {};

  const {
    // arrays de notas del contexto
    followUpExamNotes,
    annualWellnessNotes,
    telehealthNotes,
    hospitalDischargeNotes,

    // por si quieres mostrar loading o manejar estados luego
    followUpExamLoading,
    annualWellnessLoading,
    telehealthLoading,
    hospitalDischargeLoading,
  } = advancedMdContext;

  const anyLoading =
    followUpExamLoading ||
    annualWellnessLoading ||
    telehealthLoading ||
    hospitalDischargeLoading;

  // 1) Normalizar todas las notas
  const normalizedNotes = useMemo(() => {
    const all = [];

    if (Array.isArray(followUpExamNotes)) {
      all.push(...followUpExamNotes.map((n) => normalizeFollowUpExam20(n)));
    }

    if (Array.isArray(annualWellnessNotes)) {
      all.push(...annualWellnessNotes.map((n) => normalizeAnnualWellness20(n)));
    }

    if (Array.isArray(hospitalDischargeNotes)) {
      all.push(
        ...hospitalDischargeNotes.map((n) =>
          normalizeHospitalDischargeFollowUp(n)
        )
      );
    }

    if (Array.isArray(telehealthNotes)) {
      all.push(...telehealthNotes.map((n) => normalizeTelehealth20(n)));
    }

    return all;
  }, [followUpExamNotes, annualWellnessNotes, hospitalDischargeNotes, telehealthNotes]);

  // 2) Agrupar por provider
  const providersSummary = useMemo(() => {
    if (!normalizedNotes.length) return [];

    const map = new Map();

    normalizedNotes.forEach((note) => {
      const baseName = note.providerName || 'Unknown Provider';
      const key = (note.providerId || baseName).toLowerCase().trim();
      if (!key) return;

      if (!map.has(key)) {
        map.set(key, {
          providerId: note.providerId,
          name: baseName,
          specialty: '', // si luego tienes directorio de providers, lo llenamos aquí
          phone: note.phone || '',
          visits: 0,
          lastVisit: null,
          notes: [],
        });
      }

      const item = map.get(key);
      item.visits += 1;
      item.notes.push(note);

      if (!item.phone && note.phone) {
        item.phone = note.phone;
      }

      if (note.encounterDate) {
        const current = item.lastVisit ? new Date(item.lastVisit) : null;
        const next = new Date(note.encounterDate);
        if (!current || next > current) {
          item.lastVisit = note.encounterDate;
        }
      }
    });

    return Array.from(map.values()).sort((a, b) => {
      if (!a.lastVisit) return 1;
      if (!b.lastVisit) return -1;
      return new Date(b.lastVisit) - new Date(a.lastVisit);
    });
  }, [normalizedNotes]);

  // 3) Estado del diálogo
  const [openProvider, setOpenProvider] = useState(null);
  const [dialogTab, setDialogTab] = useState(0);

  const handleOpenProvider = (provider) => {
    setOpenProvider(provider);
    setDialogTab(0); // siempre abrir en "Summary"
  };

  const handleCloseProvider = () => {
    setOpenProvider(null);
  };


  if (anyLoading && !providersSummary.length) {
    return (
      <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
        <CardHeader
          title="Providers Seen"
          titleTypographyProps={{ variant: 'h5', align: 'center' }}
          action={<PersonIcon fontSize="large" color="primary" />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body2">Loading clinical notes…</Typography>
        </CardContent>
      </Card>
    );
  }

  if (!providersSummary.length) return null;

  return (
    <>
      <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
        <CardHeader
          title="Providers Seen"
          titleTypographyProps={{ variant: 'h5', align: 'center' }}
          action={<PersonIcon fontSize="large" color="primary" />}
        />
        <Divider />
        <CardContent>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <PersonIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Provider
                  </TableCell>
                  <TableCell>
                    <WorkOutlineIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Specialty
                  </TableCell>
                  <TableCell>Visits</TableCell>
                  <TableCell>
                    <CalendarTodayIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Last Visit
                  </TableCell>
                  <TableCell>
                    <PhoneIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Phone
                  </TableCell>
                  <TableCell align="center">
                    <NotesIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Notes
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {providersSummary.map((prov) => (
                  <TableRow key={prov.name} hover>
                    <TableCell>{prov.name}</TableCell>
                    <TableCell>{prov.specialty || '—'}</TableCell>
                    <TableCell>{prov.visits}</TableCell>
                    <TableCell>
                      {prov.lastVisit
                        ? new Date(prov.lastVisit).toLocaleDateString()
                        : '—'}
                    </TableCell>
                    <TableCell>{prov.phone || '—'}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenProvider(prov)}
                      >
                        <NotesIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </Card>

      {/* Dialog con el resumen de notas del provider */}
      <Dialog
        open={!!openProvider}
        onClose={handleCloseProvider}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {openProvider?.name} – Clinical Notes
        </DialogTitle>

        <DialogContent dividers>
          {/* Tabs de la vista */}
          <Tabs
            value={dialogTab}
            onChange={(_, value) => setDialogTab(value)}
            variant="fullWidth"
            sx={{ mb: 2 }}
          >
            <Tab label="Summary" />
            <Tab label="Dx & Plan" />
          </Tabs>

          {/* ====== TAB 0: SUMMARY ====== */}
          {dialogTab === 0 && (
            <>
              {openProvider?.notes?.length ? (
                <List dense>
                  {openProvider.notes.map((note, idx) => (
                    <ListItem
                      key={`${note.templateName || note.noteType}-summary-${idx}`}
                      alignItems="flex-start"
                    >
                      <ListItemText
                        primary={
                          <>
                            <Typography variant="subtitle2" component="span">
                              {note.encounterDate
                                ? new Date(note.encounterDate).toLocaleDateString()
                                : 'Unknown date'}
                            </Typography>
                            <Typography
                              variant="caption"
                              component="span"
                              sx={{ ml: 1 }}
                            >
                              ({note.noteType})
                            </Typography>
                          </>
                        }
                        secondary={
                          <Typography variant="body2">
                            {note.summary}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2">
                  No clinical notes found for this provider.
                </Typography>
              )}
            </>
          )}

          {/* ====== TAB 1: DX & PLAN ====== */}
          {dialogTab === 1 && (
            <>
              {openProvider?.notes?.some(
                (n) => n.diagnosisText || n.planText
              ) ? (
                <List dense>
                  {openProvider.notes.map((note, idx) => (
                    <ListItem
                      key={`${note.templateName || note.noteType}-dxplan-${idx}`}
                      alignItems="flex-start"
                    >
                      <ListItemText
                        primary={
                          <>
                            <Typography variant="subtitle2" component="span">
                              {note.encounterDate
                                ? new Date(note.encounterDate).toLocaleDateString()
                                : 'Unknown date'}
                            </Typography>
                            <Typography
                              variant="caption"
                              component="span"
                              sx={{ ml: 1 }}
                            >
                              ({note.noteType})
                            </Typography>
                          </>
                        }
                        secondary={
                          <>
                            {note.diagnosisText && (
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>Diagnosis:</strong>{' '}
                                {note.diagnosisText}
                              </Typography>
                            )}
                            {note.planText && (
                              <Typography variant="body2">
                                <strong>Plan:</strong>{' '}
                                {note.planText}
                              </Typography>
                            )}
                            {!note.diagnosisText && !note.planText && (
                              <Typography variant="body2" color="text.secondary">
                                No diagnosis/plan details captured for this note.
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2">
                  No diagnosis / plan information available for these notes.
                </Typography>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseProvider}>Close</Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default ProviderEncounterTable;
